import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ChevronLeft, Pencil } from "lucide-react";
import { Button, Input, Textarea, Card } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { TaskStatusPill } from "./components/TaskStatusPill";
import { TaskMetadataSidebar } from "./components/TaskMetadataSidebar";
import { TaskCommentsThread } from "./components/TaskCommentsThread";
import {
  useUpdateWorkTask,
  useWorkTask,
} from "./hooks/useWorkTasks";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { ApiError } from "@/api/client";

export function TaskDetailPage() {
  const { id } = useParams<{ id: string }>();
  const taskId = id ?? "";
  const navigate = useNavigate();
  const { toast } = useToast();

  const taskQ = useWorkTask(taskId);
  const update = useUpdateWorkTask(taskId);

  // Default breadcrumb is URL-derived, which renders the raw task GUID as the
  // trailing crumb. Override with the task name once loaded; when the title is
  // empty/null we leave the default in place.
  const taskName = taskQ.data?.title?.trim();
  useBreadcrumbs(
    taskName
      ? [{ label: "Tasks", href: "/tasks" }, { label: taskName }]
      : null,
  );

  const [titleEditing, setTitleEditing] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [descEditing, setDescEditing] = useState(false);
  const [descDraft, setDescDraft] = useState("");

  useEffect(() => {
    if (taskQ.data) {
      setTitleDraft(taskQ.data.title);
      setDescDraft(taskQ.data.description ?? "");
    }
  }, [taskQ.data]);

  if (taskQ.isLoading) {
    return (
      <div className="text-small text-gray-mid">Loading task…</div>
    );
  }
  if (taskQ.isError) {
    return (
      <div className="text-small text-status-error">
        Could not load task. {(taskQ.error as Error).message}
      </div>
    );
  }
  const task = taskQ.data;
  if (!task) return null;

  const handleErr = (err: unknown, fallback: string) => {
    const msg = err instanceof ApiError ? err.errors?.[0] ?? err.message : fallback;
    toast({ message: msg, tone: "error" });
  };

  const saveTitle = async () => {
    const next = titleDraft.trim();
    if (!next || next === task.title) {
      setTitleEditing(false);
      setTitleDraft(task.title);
      return;
    }
    try {
      await update.mutateAsync({ title: next });
      setTitleEditing(false);
    } catch (e) {
      handleErr(e, "Could not update title.");
    }
  };

  const saveDesc = async () => {
    const next = descDraft;
    if ((next || "") === (task.description ?? "")) {
      setDescEditing(false);
      return;
    }
    try {
      await update.mutateAsync({ description: next });
      setDescEditing(false);
    } catch (e) {
      handleErr(e, "Could not update description.");
    }
  };

  return (
    <div className="flex flex-col gap-space-4">
      <div className="text-label text-gray-mid">
        <Link to="/tasks" className="inline-flex items-center gap-space-1 hover:text-onyx">
          <ChevronLeft size={14} aria-hidden /> Back to tasks
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-space-4">
        <Card>
          <div className="flex items-start justify-between gap-space-3 mb-space-3">
            {titleEditing ? (
              <div className="flex-1 flex flex-col gap-space-2">
                <Input
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  autoFocus
                  aria-label="Title"
                />
                <div className="flex justify-end gap-space-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    type="button"
                    onClick={() => {
                      setTitleDraft(task.title);
                      setTitleEditing(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button size="sm" type="button" onClick={saveTitle}>
                    Save
                  </Button>
                </div>
              </div>
            ) : (
              <h1 className="font-display font-extrabold text-h2 text-onyx m-0 flex-1">
                {task.title}
                <button
                  type="button"
                  onClick={() => setTitleEditing(true)}
                  aria-label="Edit title"
                  className="ml-space-2 text-gray-mid hover:text-gold-shadow align-middle"
                >
                  <Pencil size={16} aria-hidden />
                </button>
              </h1>
            )}
            <TaskStatusPill status={task.status} percent={task.progressPercent} />
          </div>

          <div className="mb-space-4">
            {descEditing ? (
              <div className="flex flex-col gap-space-2">
                <Textarea
                  value={descDraft}
                  onChange={(e) => setDescDraft(e.target.value)}
                  aria-label="Description"
                />
                <div className="flex justify-end gap-space-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    type="button"
                    onClick={() => {
                      setDescDraft(task.description ?? "");
                      setDescEditing(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button size="sm" type="button" onClick={saveDesc}>
                    Save
                  </Button>
                </div>
              </div>
            ) : task.description ? (
              <button
                type="button"
                onClick={() => setDescEditing(true)}
                className="text-left w-full prose prose-sm max-w-none text-onyx hover:bg-white-warm rounded-md p-space-2 -m-space-2"
                aria-label="Edit description"
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{task.description}</ReactMarkdown>
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setDescEditing(true)}
                className="text-small text-gray-mid italic hover:text-onyx"
              >
                Add a description…
              </button>
            )}
          </div>

          <hr className="border-0 border-t border-border-light my-space-3" />

          <TaskCommentsThread taskId={task.id} />
        </Card>

        <TaskMetadataSidebar
          task={task}
          onDeleted={() => {
            navigate("/tasks");
          }}
        />
      </div>
    </div>
  );
}
